import Bugsnag from "@bugsnag/js";
import { client } from "@nju/http-client";
import { isError, isOk, ok } from "@nju/result";
import { getScopeConfig, IScope } from "@nju/scope";
import { info } from "../info";
import { setAuthInfoInStorage } from "../setAuthIntoInStorage";
import { ILoginResult } from "./types";
import { serverResponse } from "./validators";

interface IParams {
  refreshToken: string;
  canFail?: boolean;
  scope: IScope;
}

export async function loginWithRefreshToken({
  refreshToken,
  canFail,
  scope,
}: IParams): Promise<ILoginResult> {
  const body = new URLSearchParams();
  const { clientId, realm } = getScopeConfig(scope);

  body.append("grant_type", "refresh_token");
  body.append("client_id", clientId || "unknown");
  body.append("refresh_token", refreshToken);

  const result = await client(
    `${process.env.NEXT_PUBLIC_IAM_HOST}/auth/realms/${realm}/protocol/openid-connect/token`,
    {
      method: "post",
      validator: serverResponse,
      doNotRefreshTokenOn401: true,
      scope,
      headers: {
        "Accept-Language": "pl",
        Cookie: "KEYCLOAK_LOCALE=pl",
      },
      body,
    },
  );

  if (isError(result)) {
    if (!canFail) {
      Bugsnag.notify(result.err);
    }
    return result;
  }

  const now = Date.now();

  const output = {
    accessToken: result.value.access_token,
    refreshToken: result.value.refresh_token,
    accessTokenExpirationDate: now + result.value.expires_in * 1000,
    refreshTokenExpirationDate: now + result.value.refresh_expires_in * 1000,
  };

  setAuthInfoInStorage(output, scope);

  if (scope !== "sales") {
    const userInfo = await info();

    if (isOk(userInfo)) {
      Bugsnag.setUser(
        userInfo.value.id,
        userInfo.value.email,
        `${userInfo.value.firstName} ${userInfo.value.lastName}`,
      );
    }
  }

  return ok(output);
}
